import { useState } from "react";
import {
  classNames,
  isReadOnlyUser,
} from "../../../shared/utility";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import WipOutgoingSubTable1 from "./outgoingSubtables/WipOutgoingSubTable1";
import TableLoadingSkeleton from "../../shared/TableLoadingSkeleton";
import CopyableIdentifier from "../../controls/CopyableIdentifier";
import Button from "../../controls/Button";
import NumberInputText from "../../controls/inputs/NumberInputText";
import { useCellSaveState } from "../../../hooks/useCellSaveState";
import FinanceHelpLabel from "../../finance/FinanceHelpLabel";

export default function OutgoingWipItemsTableComponent(props) {
  const {
    isEdit,
    loading,
    values,
    openSlideForManufacturedItemDetails,
    onAskDeleteWipOutputItem,
    openAddPriceGroupModal,
    setOutgoingSelectedItems,
    outgoingSelectedItems,
    calculateCostForWipOutgoingItems,
    fetchOutgoingRowDetails,
    outgoingLoadingRows,
    outgoingRowDetails,
    fetchOutgoingSubRowDetails,
    outgoingSubRowDetails,
    outgoingSubLoadingRows,
    setOutgoingRowDetails,
    setOutgoingSubRowDetails,
    deleteSelectedItems = [],
    setDeleteSelectedItems,
    askDeleteSelectedItems,
    onWipItemTotalCostEditComplete,
  } = props;

  const lotNumberBody = (item) => (
    <CopyableIdentifier value={item.lot_number} description="lot number" />
  );

  const [expandedRows, setExpandedRows] = useState(null);
  const { getSaveStatus, saveCell } = useCellSaveState();
  const allowExpansion = () => {
    return true;
  };

  const getRowClassName = (data) => {
    if (!data.pricing_group) return "";

    const colorMap = {
      1: "bg-red-50",
      2: "bg-orange-50",
      3: "bg-emerald-50",
      4: "bg-yellow-50",
      5: "bg-lime-50",
      6: "bg-green-50",
      7: "bg-cyan-50",
      8: "bg-rose-50",
      9: "bg-violet-50",
      10: "bg-pink-50",
      11: "bg-purple-50",
      12: "bg-accent-soft",
      13: "bg-teal-50",
      14: "bg-amber-50",
      15: "bg-blue-50 ",
    };

    return colorMap[data.pricing_group] || "";
  };

  const onRowExpand = (event) => {
    const rowData = event.data;
    if (!outgoingRowDetails[rowData.unique_id]) {
      fetchOutgoingRowDetails(rowData);
    }
  };

  const rowExpansionTemplate = (data) => {
    return (
      <WipOutgoingSubTable1
        data={data}
        outgoingLoadingRows={outgoingLoadingRows}
        outgoingRowDetails={outgoingRowDetails}
        fetchOutgoingSubRowDetails={fetchOutgoingSubRowDetails}
        outgoingSubRowDetails={outgoingSubRowDetails}
        outgoingSubLoadingRows={outgoingSubLoadingRows}
        openSlideForManufacturedItemDetails={
          openSlideForManufacturedItemDetails
        }
        onAskDeleteWipOutputItem={onAskDeleteWipOutputItem}
        selectedItems={deleteSelectedItems}
        setSelectedItems={setDeleteSelectedItems}
        isReadOnly={isReadOnly}
      />
    );
  };

  const bodyProductName = (item) => {
    return (
      <p>
        {item.product_name} <br />
        {item.plu}
      </p>
    );
  };

  const bodyParentTotalWeight = (item) => {
    return (
      <NumberInputText
        value={item.total_weight_in_kg}
        aria-label={`Total weight for ${item.product_name}`}
        className="receiving-table-number"
        inputClassName="receiving-table-input"
        disabled
        minFractionDigits={4}
        maxFractionDigits={4}
        suffix=" kg"
      />
    );
  };

  const bodyParentCartonCount = (item) => (
    <NumberInputText
      value={item.carton_count}
      aria-label={`Carton count for ${item.product_name}`}
      className="receiving-table-number"
      inputClassName="receiving-table-input"
      disabled
      minFractionDigits={0}
      maxFractionDigits={0}
      suffix=" cartons"
    />
  );

  const bodyParentTotalPieces = (item) => {
    return (
      <NumberInputText
        value={item.total_pieces}
        aria-label={`Total pieces for ${item.product_name}`}
        className="receiving-table-number"
        inputClassName="receiving-table-input"
        disabled
        minFractionDigits={0}
        maxFractionDigits={0}
        suffix=" pieces"
      />
    );
  };

  const itemCostPercentage = (item) => {
    return <p>{Number(item.production_cost_percentage).toFixed(2)}%</p>;
  };

  const itemTotalCostInDollars = (item) => {
    return (
      <NumberInputText
        value={item.total_cost}
        aria-label={`Total cost for ${item.product_name}`}
        className="receiving-table-number"
        inputClassName="receiving-table-input"
        disabled={!isEdit || isReadOnly}
        saveStatus={getSaveStatus(`outgoing:${item.unique_id}:total_cost`)}
        onCommit={(value) => saveCell(`outgoing:${item.unique_id}:total_cost`, () =>
          onWipItemTotalCostEditComplete({ ...item, total_cost: value, direction: "outgoing" })
        )}
        mode="currency" currency="USD" locale="en-US" min={0}
        minFractionDigits={2} maxFractionDigits={2}
      />
    );
  };

  const itemPricingGroup = (item) => {
    if (item.pricing_group_percentage == null) {
      return null;
    }
    return <p>{Number(item.pricing_group_percentage).toFixed(0)}%</p>;
  };

  const isReadOnly = isReadOnlyUser();

  return (
    <div className="flow-root">
      <div className="overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8 space-y-2">
          <div className="w-full flex items-center gap-2">
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex-grow">
              Outgoing items
            </h3>
            {!isReadOnly && (
              <Button
                variant={outgoingSelectedItems.length > 0 ? "primary" : "secondary"}
                disabled={outgoingSelectedItems.length === 0}
                className={classNames(
                  isEdit ? "" : "hidden",
                  "disabled:border-border disabled:bg-surface disabled:text-brand-muted disabled:opacity-100",
                )}
                onClick={() => {
                  openAddPriceGroupModal();
                  setExpandedRows(false);
                }}
              >
                Pricing group ({outgoingSelectedItems.length})
              </Button>
            )}
            {isEdit ? <FinanceHelpLabel label="Pricing group" labelVisible={false} help="Assigns the selected outgoing product groups a shared percentage of the WIP input cost." /> : null}
            <Button
              variant="secondary"
              className={isEdit ? "" : "hidden"}
              onClick={() => {
                calculateCostForWipOutgoingItems();
                setExpandedRows(false);
                setOutgoingRowDetails({});
                setOutgoingSubRowDetails({});
              }}
            >
              Calculate cost
            </Button>
            {isEdit ? <FinanceHelpLabel label="Calculate cost" labelVisible={false} help="Recalculates outgoing item costs from incoming material cost and the assigned pricing percentages." /> : null}
            {!isReadOnly ? (
              <Button
                variant={deleteSelectedItems.length > 0 ? "danger" : "secondary"}
                disabled={deleteSelectedItems.length === 0}
                className="ml-2 disabled:border-border disabled:bg-surface disabled:text-brand-muted disabled:opacity-100"
                onClick={askDeleteSelectedItems}
              >
                Delete ({deleteSelectedItems.length})
              </Button>
            ) : null}
          </div>
          {loading ? (
            <TableLoadingSkeleton rows={5} columns={4} />
          ) : (
            <div className="overflow-hidden card-custom bg-white">
              <DataTable
                value={values.outgoing_items}
                expandedRows={expandedRows}
                onRowToggle={(e) => {
                  setExpandedRows(e.data);
                }}
                onRowExpand={onRowExpand}
                rowExpansionTemplate={rowExpansionTemplate}
                dataKey="unique_id"
                tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm wip-outgoing-items-table"
                cellClassName="p-2 border-1-bottom-gray text-sm"
                rowClassName={getRowClassName}
                selectionMode="checkbox"
                selection={outgoingSelectedItems}
                onSelectionChange={(e) => setOutgoingSelectedItems(e.value)}
              >
                <Column
                  selectionMode="multiple"
                  headerStyle={{ width: "1px" }}
                ></Column>
                <Column
                  header="Product Name"
                  body={bodyProductName}
                  style={{ width: "25%" }}
                />
                <Column
                  header="Lot Number"
                  body={lotNumberBody}
                  style={{ width: "15%" }}
                />
                <Column
                  header="Carton Count"
                  body={bodyParentCartonCount}
                  align="right"
                  alignHeader="right"
                />
                <Column
                  header="Total Weight (kg)"
                  body={bodyParentTotalWeight}
                  align="right"
                  alignHeader="right"
                />
                <Column
                  header="Total Pieces"
                  body={bodyParentTotalPieces}
                  align="right"
                  alignHeader="right"
                />
                <Column
                  header="Cost %"
                  body={itemCostPercentage}
                  align="right"
                  alignHeader="right"
                  headerStyle={{ minWidth: "5rem", whiteSpace: "nowrap" }}
                  bodyStyle={{ whiteSpace: "nowrap" }}
                />
                <Column
                  header="Cost $"
                  body={itemTotalCostInDollars}
                  align="left"
                  alignHeader="left"
                />
                <Column
                  header="Pricing Group %"
                  body={itemPricingGroup}
                  style={{ textAlign: "center" }}
                />
                <Column expander={allowExpansion} style={{ width: "6%" }} />
              </DataTable>
              {values.outgoing_info ? (
                <div className="p-4 space-y-3">
                  <div className="flex justify-end gap-4">
                    <p className="text-md font-bold">
                      Total cartons: {values.outgoing_info.carton_count}
                    </p>
                    <p>
                      Total weight: {values.outgoing_info.total_weight_in_kg} kg
                    </p>
                    <p>
                      Total pieces: {values.outgoing_info.total_pieces} pieces
                    </p>
                  </div>
                  <div className="flex justify-end gap-4">
                    <p
                      className={classNames(
                        values.outgoing_info.total_cost_percentage > 100
                          ? "text-red-600"
                          : parseFloat(
                                values.outgoing_info.total_cost_percentage,
                              ) === parseFloat(100)
                            ? "text-green-600"
                            : null,
                        "text-md font-bold",
                      )}
                    >
                      Total cost percentage:{" "}
                      {Number(
                        values.outgoing_info.total_cost_percentage,
                      ).toFixed(2)}
                      %
                    </p>
                    <p>Total cost: ${values.outgoing_info.total_cost}</p>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
